import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/*
 * Hairline notice: inline, sunken, 8px radius, icon plus text. Tints only for
 * success, warning and danger.
 */
const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-md bg-bg-sunken px-3 py-2.5 text-left text-[13px] leading-[18px] text-fg-2 has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2.5 *:[svg]:row-span-2 *:[svg]:translate-y-px *:[svg]:text-fg-3 *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "",
        success: "bg-ok-tint text-foreground *:[svg]:text-ok",
        warning: "bg-warn-tint text-foreground *:[svg]:text-warn",
        destructive:
          "bg-danger-tint text-foreground *:[svg]:text-danger *:data-[slot=alert-title]:text-danger",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-medium text-foreground group-has-[>svg]/alert:col-start-2 [&_a]:text-link [&_a]:underline-offset-[3px] [&_a]:hover:underline",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-pretty group-has-[>svg]/alert:col-start-2 [&_a]:font-medium [&_a]:text-link [&_a]:underline-offset-[3px] [&_a]:hover:underline [&_p:not(:last-child)]:mb-2",
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-1.5 right-2", className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
